import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppContext } from '../context/AppContext';
import { useAIAvatarSession } from '../context/AIAvatarSessionContext';
import { useUserData } from './useUserData';
import { usePodData } from './usePodData';
import { regenerateQRCode } from '../apis/qr';
import { getSavedPodIP } from '../utils/helpers';
import { markSessionAwaitingReport } from '../utils/podSessionReport';

/**
 * Hard reset back to this pod's welcome screen, handing the next user a clean
 * SPA. Only guests need it: non-guests get the same effect from the OIDC
 * signout redirect, which tears the app down on its way out.
 *
 * It runs when the report modal is closed rather than when the session ends,
 * so the SPA — and with it the WebSocket carrying PodSessionReport — stays
 * alive long enough to receive the report.
 */
export const resetPodForNextUser = (): void => {
    const sessionStoredPodIp = getSavedPodIP();
    window.location.replace(sessionStoredPodIp ? `/${sessionStoredPodIp}` : '/');
};

export function useEndSession() {
    const navigate = useNavigate();
    const { endSessionAndLogout } = useAuth();
    const { resetState } = useAppContext();
    const { stopSession } = useAIAvatarSession();
    const { userData } = useUserData();
    const { podId, podIP } = usePodData();
    const [isEnding, setIsEnding] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const sessionId = userData?.sessionId;

    const requestEndSession = () => {
        setShowConfirm(true);
    };

    const confirmEndSession = async () => {
        if (isEnding) return;
        setIsEnding(true);
        const isGuest = userData?.isGuest;
        const sessionStoredPodIp = getSavedPodIP();

        // The PodSessionReport event arrives after this session is gone —
        // persist its id now, while we still have it, so the report can be
        // matched once it lands (survives a refresh / the OIDC signout redirect).
        markSessionAwaitingReport(sessionId, { isGuest });

        try {
            await stopSession();
            if (podId) {
                try {
                    await regenerateQRCode(podId);
                } catch (error) {
                    console.error('Failed to regenerate QR code:', error);
                }
            }
            await endSessionAndLogout(sessionId, sessionStoredPodIp);
            resetState();

            // Guests skip the OIDC signout redirect, so the SPA stays up — and
            // that's deliberate: staying alive keeps the WebSocket connected, so
            // the PodSessionReport event isn't missed. The hard reset that hands
            // the next user a clean pod runs when they close the report instead
            // (see SessionReportModal).
            navigate('/session-report');
        } catch (error) {
            console.error('End session failed:', error);
        } finally {
            setIsEnding(false);
            setShowConfirm(false);
        }
    };

    const cancelEndSession = () => {
        setShowConfirm(false);
    };

    return {
        showConfirm,
        isEnding,
        requestEndSession,
        confirmEndSession,
        cancelEndSession,
    };
}
